import React, { useContext, useEffect, useState } from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { axiosAuth } from '../../../api';
import { Col, Form, Row } from 'react-bootstrap';
import { ThemeContext } from '../../../auth';
import Swal from 'sweetalert2';
import { useLocation } from 'react-router-dom';

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function ChartDonutProduct({ id, accessToken, selectedDate }) {

    const { theme } = useContext(ThemeContext);
    const location = useLocation();

    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');

    const showingDefault = `${year}-${month}`;

    const [monthlyProductAmount, setMonthlyProductAmount] = useState({});

    const retrieveProductMonthly = () => {

        axiosAuth.get(

            location.pathname.startsWith('/dashboard-spv') ?
                `/api/analytics/monthly/products?month=${month}&year=${year}` :
                `/api/analytics/monthly/products?userId=${id}&month=${month}&year=${year}`
            ,

            {
                headers:
                {
                    'Access-Control-Allow-Origin': '*',
                    'Content-Type': 'application/json',
                    withCredentials: true,
                    Authorization: `Bearer ${accessToken}`,
                },

            })
            .then(res => {

                setMonthlyProductAmount(res.data.data);

            }).catch(err => {
                console.error(err);

                Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: 'Failed to collect data, try again later!',
                });

            })
    };

    useEffect(() => {
        if (accessToken !== undefined && id !== undefined) retrieveProductMonthly();
    }, [accessToken, id]);

    useEffect(() => {
        const fetchMonthlyProductAmount = async () => {
            if (!selectedDate) return;

            const [year, month] = selectedDate.split("-");

            const monthWithoutLeadingZero = parseInt(month, 10);

            try {
                const response = await axiosAuth.get(
                    location.pathname.startsWith('/dashboard-spv') ?
                        `/api/analytics/monthly/products?month=${monthWithoutLeadingZero}&year=${year}` :
                        `/api/analytics/monthly/products?userId=${id}&month=${monthWithoutLeadingZero}&year=${year}`
                    ,
                    {
                        headers: {
                            'Access-Control-Allow-Origin': '*',
                            'Content-Type': 'application/json',
                            withCredentials: true,
                            Authorization: `Bearer ${accessToken}`,
                        },
                    }
                );

                setMonthlyProductAmount(response.data.data);

            } catch (err) {
                console.error(err);
                Swal.fire({
                    icon: "error",
                    title: "Oops...",
                    text: 'Failed to collect data, try again later!',
                });
            }
        };

        fetchMonthlyProductAmount();
    }, [selectedDate]);

    const productData = Array.isArray(monthlyProductAmount) ? monthlyProductAmount : [];

    const labels = productData.map(item => item.productName || 'Unknown');
    const totalSelling = productData.map(item => item.totalAmount || 0);

    const baseColors = [
        '#12B3ED',
        '#816fc0',
        '#e162aa',
        '#ff6d68',
        '#ffa600',
        '#e162aa',
    ];

    const repeatedColors = Array.from({ length: totalSelling.length }, (_, i) => baseColors[i % baseColors.length]);

    const data = {
        labels,
        datasets: [
            {
                label: 'Total terjual',
                fill: true,
                backgroundColor: repeatedColors,
                borderColor: repeatedColors,
                borderWidth: 0,
                data: totalSelling,
            },
        ],
    };

    const [hoveredData, setHoveredData] = useState({ text: '', color: '#000', value: 0, percentage: 0 });
    let debounceTimer;

    const handleHover = (event, elements) => {
        clearTimeout(debounceTimer);

        debounceTimer = setTimeout(() => {
            if (elements && elements.length > 0) {
                const index = elements[0]?.index;
                if (index !== undefined && index !== null) {
                    const value = data.datasets[0].data[index];
                    const label = data.labels[index];
                    const backgroundColor = data.datasets[0].backgroundColor[index];
                    const total = data.datasets[0].data.reduce((acc, curr) => acc + curr, 0);
                    const percentage = ((value / total) * 100).toFixed(2);

                    setHoveredData({
                        text: label,
                        value: value,
                        percentage: percentage,
                        color: backgroundColor,
                    });
                }
            } else {
                setHoveredData({ text: '', color: '#000', value: 0, percentage: 0 });
            }
        }, 70);
    };

    useEffect(() => {
        return () => {
            clearTimeout(debounceTimer);
            setHoveredData({ text: '', color: '#000', value: 0, percentage: 0 });
        };
    }, []);

    const config = {
        type: 'doughnut',
        cutout: '80%',
        data: data,
        maintainAspectRatio: false,
        width: 80,
        height: 80,
        elements: {
            arc: {
                borderWidth: 1,
            },
        },
        plugins: {
            tooltip: {
                enabled: false,
            },
            legend: {
                display: false,
            },
        },
        Poppinsactions: {
            mode: 'index',
            Poppinssect: false,
        },
        onHover: handleHover,
    };

    const formatCurrency = (amount) => {
        if (amount < 1000000000) {
            return (amount / 1000000).toFixed(1) + ' Million';
        } else {

            return (amount / 1000000000).toFixed() + ' Billion';
        }
    };

    const formattedtotal = formatCurrency(hoveredData.value);

    const formatMonthName = (value) => {
        if (!value) return '';
        const date = new Date(`${value}-01`);
        return date.toLocaleString('id-ID', { month: 'long', year: 'numeric' });
    };

    return (
        <div>
            <Row className="align-items-center">
                <Col xs={6} className='text-start my-auto'>
                    <p
                        className="ps-1 pt-2 text-start"
                        style={{
                            marginBottom: '0px',
                            fontFamily: 'Poppins-Medium',
                            fontSize: '18px',
                            color: theme === 'light' ? '#FFFFFF' : '#222',
                        }}
                    >
                        Monthly Product Amount
                    </p>
                </Col>
                <Col xs={6} className='d-flex justify-content-end align-items-center gap-3'>
                    <span style={{ color: theme === 'light' ? '#c5c5c5ff' : '#c5c5c5ff', fontSize: '16px', fontWeight: 500, fontFamily: 'Poppins-Light' }}>
                        {formatMonthName(!selectedDate ? showingDefault : selectedDate)}
                    </span>
                </Col>
            </Row>
            <div className='mx-auto mb-3'
                style={{ position: 'relative', width: '200px', height: '200px', marginTop: '17px' }}
            >
                <Doughnut
                    data={data}
                    options={config}
                />
                {hoveredData.text && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            textAlign: 'center',
                            color: hoveredData.color,
                        }}
                    >
                        <p style={{ fontFamily: 'Poppins-Regular', fontSize: '15px', marginBottom: '0px' }}>{formattedtotal}</p>
                        <p style={{ fontFamily: 'Poppins-Regular', fontSize: '15px', marginBottom: '0px', marginTop: '0px' }}>{hoveredData.percentage}%</p>
                    </div>
                )}
            </div>
            {hoveredData.text && (
                <div>
                    <p
                        className='mt-2 text-center'
                        style={{
                            fontFamily: 'Poppins-Bold',
                            fontSize: '17px',
                            color: hoveredData.color,
                        }}
                    >
                        {hoveredData.text}
                    </p>
                </div>
            )}
        </div>
    )
}

export default ChartDonutProduct
